import { ComponentType, FC } from 'react';
import { useExperimentVariant } from 'hooks/useExperiment';

/**
 * Props which contain a map of experiment names to variant allocations.
 * Types wishing to be wrapped with `withUseExperimentVariant` must have
 * props which conform to this.
 */
export interface IWithUseExperimentVariantProps {
  experimentVariants: Map<string, number | undefined> | undefined;
}

/**
 * A higher order component which gets experiment variants via the `useExperimentVariant` hook,
 * allowing older, class based components to participate in experiments, with proper context
 * checks and analytics integrations.
 *
 * The wrapped component is provided the value as a map entry in the `experimentVariants` prop.
 * This component can wrap itself if multiple `experimentVariants` values are required.
 *
 * withUseExperimentVariant(
 *   'ExperimentA',
 *   withUseExperimentVariant(
 *     'ExperimentB',
 *      MyComponent
 *   )
 * )
 *
 * In this example, both `ExperimentA` and `ExperimentB` variants will be set in
 * the wrapped component's props.
 *
 * @param experimentName An experiment name to resolve and set in the `experimentVariants` map
 * @param WrappedComponent The component type to wrap
 * @returns The wrapped component
 */
export const withUseExperimentVariant = <P extends object & IWithUseExperimentVariantProps>(
  experimentName: string,
  WrappedComponent: ComponentType<P>
): FC<P> => {
  const WrappedWithUseExperimentVariant = (props: P) => {
    // If there are existing props maintain them, this allows the
    // wrapped component to receive multiple experiment variants.
    const variants = props.experimentVariants ?? new Map<string, number | undefined>();
    variants.set(experimentName, useExperimentVariant(experimentName));
    return <WrappedComponent {...props} experimentVariants={variants} />;
  };

  const componentName = WrappedComponent.displayName || WrappedComponent.name || 'WrappedComponent';
  WrappedWithUseExperimentVariant.displayName = `withUseExperimentVariant(${componentName})`;

  return WrappedWithUseExperimentVariant;
};
